/**
 * Approve / reject many pending trigger actions in sequence.
 */
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

export interface BulkApprovalControlsProps {
  pendingIds: string[];
  onDecided: () => void;
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  /** Shown before the action buttons. */
  label: string;
  testId?: string;
}

export function BulkApprovalControls({
  pendingIds,
  onDecided,
  onApprove,
  onReject,
  label,
  testId = "bulk-approval",
}: BulkApprovalControlsProps) {
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const n = pendingIds.length;

  const runAll = async (kind: "approve" | "reject") => {
    setBusy(kind);
    setError(null);
    const failures: string[] = [];
    for (const id of pendingIds) {
      try {
        if (kind === "approve") await onApprove(id);
        else await onReject(id);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Action failed";
        if (/not pending|already approved|already rejected/i.test(msg)) continue;
        failures.push(msg);
      }
    }
    setBusy(null);
    if (failures.length > 0) {
      setError(`${failures.length} of ${n} failed — refresh and retry.`);
    } else {
      onDecided();
    }
  };

  if (n === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-amber-50/90 dark:bg-amber-900/20 border border-amber-200/80 dark:border-amber-800/50"
      data-testid={testId}
    >
      <p className="text-xs font-medium text-amber-900 dark:text-amber-100">
        {label}
      </p>
      <div className="flex flex-col items-end gap-1">
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 text-right max-w-xs">
            {error}
          </p>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void runAll("approve")}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faCheck} />
            {busy === "approve" ? "Approving…" : `Approve all (${n})`}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void runAll("reject")}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faXmark} />
            {busy === "reject" ? "Rejecting…" : `Reject all (${n})`}
          </button>
        </div>
      </div>
    </div>
  );
}
